import ZozometryViewer, {
  ShowBodyBehaviors,
  CAMERA_DISTANCE,
  CAMERA_DISTANCE_MIN_STRETCH,
  CAMERA_DISTANCE_MAX_STRETCH,
} from "@zozonz/simplified-mesh-viewer"
import { useEffect, useRef, useState } from "react"
import {
  MeshViewWrapper,
  MeshContainer,
  LoadingOverlay,
  Spinner,
  WeightInfoBox,
  FullViewLabel,
  FullViewText,
} from "./styled"
import WeightInfo from "components/onboarding/Goal/WeightInfo"
import { getUSDisplayDateWithYear } from "utils/dateTime"
import { IGoal, IMesh } from "features/onboarding/types"

interface MeshViewerProps {
  measurementMesh: IMesh
  targetMesh: IMesh
  loading?: boolean
  isFullScreen: boolean
  setIsFullScreen: (isFullScreen: boolean) => void
  displayedStartWeight: string
  displayedTargetWeight: string
  goal?: IGoal
  /** Camera distance when not in fullscreen. Smaller = more zoomed in. Default: 3.0 */
  zoomDistance?: number
}

const MeshViewer = (props: MeshViewerProps) => {
  const {
    measurementMesh,
    targetMesh,
    loading = false,
    isFullScreen,
    setIsFullScreen,
    displayedStartWeight,
    displayedTargetWeight,
    goal,
    zoomDistance = CAMERA_DISTANCE,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<ZozometryViewer | null>(null)

  const [isViewerLoading, setIsViewerLoading] = useState(true)

  const applyZoomDistance = (distance: number) => {
    const scene = (viewerRef.current as any)?.scene
    if (scene?.camera?.controls) {
      scene.camera.controls.minDistance = distance
      scene.camera.controls.maxDistance = distance
      scene.camera.controls.update()
    }
  }

  const handleFullscreenChange = (isFull: boolean) => {
    const viewer = viewerRef.current
    if (!viewer) return

    setIsFullScreen(isFull)

    const scene = (viewer as any).scene
    if (scene?.camera?.controls) {
      scene.camera.controls.enabled = isFull
      scene.camera.controls.enablePan = false
      if (scene.camera.controls.reactiveState) {
        scene.camera.controls.reactiveState.enablePan = false
      }
    }

    if (isFull) {
      // Reset to default range for fullscreen (required for rotation to work)
      scene.camera.controls.minDistance = CAMERA_DISTANCE_MIN_STRETCH
      scene.camera.controls.maxDistance = CAMERA_DISTANCE_MAX_STRETCH
    } else {
      // Apply custom zoom distance when exiting fullscreen
      viewer.showFullBody(ShowBodyBehaviors.NoAnimation)
    }
  }

  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return

    const viewer = new ZozometryViewer({
      container: containerRef.current,
      enableZoomOnMeasurementFocusOnly: false,
      background: "transparent",
      showFloor: false,
      disableVerticalRotation: true,
    })

    const beforeId = `before-${Date.now().toString(36)}`
    const afterId = `after-${Date.now().toString(36)}`

    viewer.addScanData({
      id: beforeId,
      suitType: "suit",
      forSideBySide: true,
      meshData: {
        jointAxis: measurementMesh.axis,
        vertices: measurementMesh.vertices,
      },
    })

    viewer.addScanData({
      id: afterId,
      suitType: "suit",
      forSideBySide: true,
      meshData: {
        jointAxis: targetMesh.axis,
        vertices: targetMesh.vertices,
      },
    })

    viewerRef.current = viewer

    const startViewer = async () => {
      await viewer.start()

      const scene = (viewer as any).scene
      if (scene?.camera?.controls) {
        scene.camera.controls.enabled = false
        scene.camera.controls.reactiveState.enableZoom = false
      }

      viewer.setSideBySideCompareIds(beforeId, afterId)
      viewer.setShowSideBySideComparison(true, true)

      // TODO: Enable and customize comparison bar style
      // viewer.setComparisonBarOptions({
      //   width: 5,
      //   background:
      //     "linear-gradient(180deg, transparent 0%, #fff 20%, #fff 80%, transparent 100%)",
      //   paddingTop: 20,
      //   paddingBottom: 30,
      // })
      viewer.showFullBody(ShowBodyBehaviors.NoAnimation)
      applyZoomDistance(zoomDistance)
      setIsViewerLoading(false)
    }

    startViewer()

    return () => {
      viewer.destroy()
      viewerRef.current = null
    }
  }, [])

  // Handle resize events when fullscreen changes
  useEffect(() => {
    const resizeInterval = setInterval(() => {
      window.dispatchEvent(new Event("resize"))
    }, 16)

    const timeout = setTimeout(() => {
      clearInterval(resizeInterval)
      window.dispatchEvent(new Event("resize"))
    }, 550)

    return () => {
      clearInterval(resizeInterval)
      clearTimeout(timeout)
    }
  }, [isFullScreen])

  const handleClick = () => {
    if (!isFullScreen) {
      handleFullscreenChange(true)
    }
  }

  return (
    <MeshViewWrapper $isFullScreen={isFullScreen} onClick={handleClick}>
      {(loading || isViewerLoading) && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}

      <MeshContainer ref={containerRef} $isFullScreen={isFullScreen} />

      {!isFullScreen && (
        <FullViewLabel>
          <FullViewText>360° View</FullViewText>
        </FullViewLabel>
      )}

      <WeightInfoBox>
        <WeightInfo
          label="start"
          type="start"
          weight={displayedStartWeight}
          date={getUSDisplayDateWithYear(goal?.startDate)}
        />

        <WeightInfo
          label="goal"
          type={goal?.typeName === "BUILDING" ? "gain" : "lose"}
          weight={displayedTargetWeight}
          date={getUSDisplayDateWithYear(goal?.reachedOn)}
        />
      </WeightInfoBox>
    </MeshViewWrapper>
  )
}

export default MeshViewer
